/**
 * Get object value by string path
 *
 * ### Example
 * ```js
 * import { getObjectValueByPath } from '@tsyl/radon'
 * const obj = {
 *   a: {
 *     b: {
 *       c: 'result',
 *     },
 *   },
 * }
 * const result = getObjectValueByPath(obj, 'a.b.c')
 * console.log(result)
 * // => result
 * ```
 *
 * @param obj - The object to access
 * @param path - Object path
 * @param fallback - Returned on error
 * @returns The object value in the path, fallback value or undefined
 */
export function getObjectValueByPath(obj, path, fallback) {
    if (obj == null || !path || typeof path !== 'string')
        return fallback;
    if (obj[path] !== undefined)
        return obj[path];
    path = path.replace(/\[(\w+)\]/g, '.$1');
    path = path.replace(/^\./, '');
    const Arraypath = path.split('.');
    const last = Arraypath.length - 1;
    for (let i = 0; i < last; i++) {
        if (obj == null) {
            return fallback;
        }
        obj = obj[Arraypath[i]];
    }
    if (obj == null)
        return fallback;
    return obj[Arraypath[last]] === undefined ? fallback : obj[Arraypath[last]];
}
/**
 * Set a object value by string path.
 * You could consider the inverse of getObjectValueByPath
 *
 * ### Example
 * ```js
 * import { setObjectValueByPath } from '@tsyl/radon'
 * const obj = { }
 * const result = setObjectValueByPath(obj, 'a.b.c', 'value')
 * console.log(result)
 * // => a: { b: { c: 'value' } } }
 * ```
 *
 * @param obj - The object to add the value
 * @param path - Object path
 * @param value the value
 * @returns the object. Note that the object is modified
 * <a target="_blank" href="https://en.wikipedia.org/wiki/In-place_algorithm">in place</a>,
 * and no copy is made.
 */
export function setObjectValueByPath(objParam, path, value) {
    if (objParam == null || !path || typeof path !== 'string')
        return objParam;
    path = path.replace(/\[(\w+)\]/g, '.$1');
    path = path.replace(/^\./, '');
    const Arraypath = path.split('.');
    let obj = objParam;
    const last = Arraypath.length - 1;
    for (let i = 0; i < last; i++) {
        if (obj[Arraypath[i]] === undefined) {
            obj[Arraypath[i]] = {};
        }
        obj = obj[Arraypath[i]];
    }
    obj[Arraypath[last]] = value;
    return objParam;
}
/**
 * if parameter is undefined, null or empty string
 * returns true,
 * but 0 or anything else returns false
 *
 * @param obj - value to check
 * @returns true or false
 */
export function isNullUndefinedOrEmptyString(val) {
    return val === null || val === '' || val === undefined;
}
/**
 * if parameter is undefined, null, empty string or empty array
 * returns true,
 * but 0 or anything else returns false
 *
 * @param obj - value to check
 * @returns true or false
 */
export function isNullUndefinedEmptyStringOrEmptyArray(val) {
    return (val === null ||
        val === '' ||
        val === undefined ||
        (Array.isArray(val) && val.length === 0));
}
/**
 * if parameter is undefined or null
 * returns true,
 * but 0, empty string or anything else returns false
 *
 * @param obj - value to check
 * @returns true or false
 */
export function isNullOrUndefined(val) {
    return val === null || val === undefined;
}
/**
 * check if value is boolean true or string 'true' (case insesitive)
 *
 * @param obj - value to check
 * @returns true or false
 */
export function isTrueOrTrueString(v) {
    return v === true || (v + '').toLowerCase() === 'true';
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoib2JqZWN0LmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2xpYi9vYmplY3QudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FzQkc7QUFDSCxNQUFNLFVBQVUsb0JBQW9CLENBQUMsR0FBUSxFQUFFLElBQVksRUFBRSxRQUFjO0lBQ3pFLElBQUksR0FBRyxJQUFJLElBQUksSUFBSSxDQUFDLElBQUksSUFBSSxPQUFPLElBQUksS0FBSyxRQUFRO1FBQUUsT0FBTyxRQUFRLENBQUE7SUFDckUsSUFBSSxHQUFHLENBQUMsSUFBSSxDQUFDLEtBQUssU0FBUztRQUFFLE9BQU8sR0FBRyxDQUFDLElBQUksQ0FBQyxDQUFBO0lBRTdDLElBQUksR0FBRyxJQUFJLENBQUMsT0FBTyxDQUFDLFlBQVksRUFBRSxLQUFLLENBQUMsQ0FBQTtJQUN4QyxJQUFJLEdBQUcsSUFBSSxDQUFDLE9BQU8sQ0FBQyxLQUFLLEVBQUUsRUFBRSxDQUFDLENBQUE7SUFDOUIsTUFBTSxTQUFTLEdBQUcsSUFBSSxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsQ0FBQTtJQUVqQyxNQUFNLElBQUksR0FBRyxTQUFTLENBQUMsTUFBTSxHQUFHLENBQUMsQ0FBQTtJQUVqQyxLQUFLLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsSUFBSSxFQUFFLENBQUMsRUFBRSxFQUFFO1FBQzdCLElBQUksR0FBRyxJQUFJLElBQUksRUFBRTtZQUNmLE9BQU8sUUFBUSxDQUFBO1NBQ2hCO1FBQ0QsR0FBRyxHQUFHLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQTtLQUN4QjtJQUNELElBQUksR0FBRyxJQUFJLElBQUk7UUFBRSxPQUFPLFFBQVEsQ0FBQTtJQUVoQyxPQUFPLEdBQUcsQ0FBQyxTQUFTLENBQUMsSUFBSSxDQUFDLENBQUMsS0FBSyxTQUFTLENBQUMsQ0FBQyxDQUFDLFFBQVEsQ0FBQyxDQUFDLENBQUMsR0FBRyxDQUFDLFNBQVMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFBO0FBQzdFLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQW1CRztBQUNILE1BQU0sVUFBVSxvQkFBb0IsQ0FBQyxRQUFhLEVBQUUsSUFBWSxFQUFFLEtBQVU7SUFDMUUsSUFBSSxRQUFRLElBQUksSUFBSSxJQUFJLENBQUMsSUFBSSxJQUFJLE9BQU8sSUFBSSxLQUFLLFFBQVE7UUFBRSxPQUFPLFFBQVEsQ0FBQTtJQUUxRSxJQUFJLEdBQUcsSUFBSSxDQUFDLE9BQU8sQ0FBQyxZQUFZLEVBQUUsS0FBSyxDQUFDLENBQUE7SUFDeEMsSUFBSSxHQUFHLElBQUksQ0FBQyxPQUFPLENBQUMsS0FBSyxFQUFFLEVBQUUsQ0FBQyxDQUFBO0lBQzlCLE1BQU0sU0FBUyxHQUFHLElBQUksQ0FBQyxLQUFLLENBQUMsR0FBRyxDQUFDLENBQUE7SUFFakMsSUFBSSxHQUFHLEdBQUcsUUFBUSxDQUFBO0lBRWxCLE1BQU0sSUFBSSxHQUFHLFNBQVMsQ0FBQyxNQUFNLEdBQUcsQ0FBQyxDQUFBO0lBQ2pDLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxJQUFJLEVBQUUsQ0FBQyxFQUFFLEVBQUU7UUFDN0IsSUFBSSxHQUFHLENBQUMsU0FBUyxDQUFDLENBQUMsQ0FBQyxDQUFDLEtBQUssU0FBUyxFQUFFO1lBQ25DLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQyxDQUFDLENBQUMsR0FBRyxFQUFFLENBQUE7U0FDdkI7UUFDRCxHQUFHLEdBQUcsR0FBRyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFBO0tBQ3hCO0lBRUQsR0FBRyxDQUFDLFNBQVMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxHQUFHLEtBQUssQ0FBQTtJQUU1QixPQUFPLFFBQVEsQ0FBQTtBQUNqQixDQUFDO0FBRUQ7Ozs7Ozs7R0FPRztBQUNILE1BQU0sVUFBVSw0QkFBNEIsQ0FBQyxHQUFRO0lBQ25ELE9BQU8sR0FBRyxLQUFLLElBQUksSUFBSSxHQUFHLEtBQUssRUFBRSxJQUFJLEdBQUcsS0FBSyxTQUFTLENBQUE7QUFDeEQsQ0FBQztBQUVEOzs7Ozs7O0dBT0c7QUFDSCxNQUFNLFVBQVUsc0NBQXNDLENBQUMsR0FBUTtJQUM3RCxPQUFPLENBQ0wsR0FBRyxLQUFLLElBQUk7UUFDWixHQUFHLEtBQUssRUFBRTtRQUNWLEdBQUcsS0FBSyxTQUFTO1FBQ2pCLENBQUMsS0FBSyxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQUMsSUFBSSxHQUFHLENBQUMsTUFBTSxLQUFLLENBQUMsQ0FBQyxDQUN6QyxDQUFBO0FBQ0gsQ0FBQztBQUVEOzs7Ozs7O0dBT0c7QUFDSCxNQUFNLFVBQVUsaUJBQWlCLENBQUMsR0FBUTtJQUN4QyxPQUFPLEdBQUcsS0FBSyxJQUFJLElBQUksR0FBRyxLQUFLLFNBQVMsQ0FBQTtBQUMxQyxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsa0JBQWtCLENBQUMsQ0FBTTtJQUN2QyxPQUFPLENBQUMsS0FBSyxJQUFJLElBQUksQ0FBQyxDQUFDLEdBQUcsRUFBRSxDQUFDLENBQUMsV0FBVyxFQUFFLEtBQUssTUFBTSxDQUFBO0FBQ3hELENBQUMifQ==